// src/pages/dashboard/Dashboard.jsx
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Statistic,
  Tag,
  Divider,
  Tooltip as AntTooltip,
} from "antd";
import {
  AppstoreOutlined,
  DatabaseOutlined,
  FireOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  ScanOutlined,
  HistoryOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import { useUI } from "@/context/UIContext";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.06 * i, duration: 0.4, ease: "easeOut" },
  }),
};

// --- Mock data (константы не пересоздаются на рендерах) ---
const inventoryTurnover = [
  { month: "Січ", turnover: 1.2 },
  { month: "Лют", turnover: 1.4 },
  { month: "Бер", turnover: 1.1 },
  { month: "Кві", turnover: 1.6 },
  { month: "Тра", turnover: 1.9 },
  { month: "Чер", turnover: 2.1 },
  { month: "Лип", turnover: 2.4 },
  { month: "Сер", turnover: 2.2 },
  { month: "Вер", turnover: 2.5 },
  { month: "Жов", turnover: 2.8 },
  { month: "Лис", turnover: 2.6 },
  { month: "Гру", turnover: 3.0 },
];

const ordersPerHour = [
  { hour: "08", qty: 2 },
  { hour: "09", qty: 5 },
  { hour: "10", qty: 7 },
  { hour: "11", qty: 10 },
  { hour: "12", qty: 12 },
  { hour: "13", qty: 8 },
  { hour: "14", qty: 9 },
  { hour: "15", qty: 6 },
  { hour: "16", qty: 4 },
  { hour: "17", qty: 3 },
];

const statusSplit = [
  { name: "New", value: 12 },
  { name: "Picking", value: 9 },
  { name: "Packed", value: 8 },
  { name: "Shipped", value: 6 },
  { name: "Hold", value: 2 },
];

const pieColors = ["#6366F1", "#22C55E", "#F59E0B", "#0EA5E9", "#EF4444"];

export default function Dashboard() {
  const navigate = useNavigate();
  const { setHeaderTitle } = useUI();

  const [showCharts, setShowCharts] = useState(false);
  const [animateCharts, setAnimateCharts] = useState(false);
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    setHeaderTitle("Дашборд");
    return () => setHeaderTitle("WHFOF");
  }, [setHeaderTitle]);

  // Монтируем графики после первого свободного слота (idle), чтобы перший скролл був плавним
  useEffect(() => {
    const schedule = (cb) => {
      if (typeof window !== "undefined") {
        // requestIdleCallback может отсутствовать в Safari/старых браузерах
        const ric = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
        const id = ric(() => cb());
        return () => {
          const cancel = window.cancelIdleCallback || clearTimeout;
          cancel(id);
        };
      }
      return () => {};
    };
    return schedule(() => {
      setShowCharts(true);
      if (!prefersReducedMotion) {
        // триггерим анимацию только при первом монтировании
        setAnimateCharts(true);
        setTimeout(() => setAnimateCharts(false), 1200);
      }
    });
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("auth_user");
    localStorage.removeItem("auth_token");
    navigate("/login", { replace: true });
  };

  return (
    <div className="relative w-full overflow-x-hidden">
      {/* Легкий фон без blur и без анимаций (не трогаем компоновку при скролле) */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(600px 420px at 10% 10%, rgba(240,171,252,0.35), transparent 60%), radial-gradient(620px 500px at 90% 90%, rgba(134,239,172,0.35), transparent 60%)",
        }}
      />

      <div
        className="p-3 sm:p-6 max-w-full"
        // content-visibility: auto экономит рендер вне вьюпорта
        style={{ contentVisibility: "auto", containIntrinsicSize: "1px 800px" }}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="flex items-center gap-2"
            style={{ willChange: "transform, opacity", transform: "translateZ(0)" }}
          >
            <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-xl bg-black text-white grid place-items-center" style={{ boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}>
              <span className="font-semibold">F</span>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Friends of Fashion</div>
              <h1 className="text-lg sm:text-2xl font-bold leading-snug break-words">WHFOF · Панель керування v1.4</h1>
            </div>
          </motion.div>

          <div className="flex sm:flex hidden items-center gap-2">
            <AntTooltip title="Вийти">
              <Button size="small" onClick={handleLogout}>
                Вихід
              </Button>
            </AntTooltip>
          </div>
        </div>

        {/* Subheader */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="mb-5"
          style={{ willChange: "transform, opacity", transform: "translateZ(0)" }}
        >
          <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
            <Tag color="green">Склад активний</Tag>
            <span className="text-gray-500">FOF · Kyiv Fulfillment</span>
            <span className="text-gray-400 hidden sm:inline">•</span>
            <span className="text-gray-500 text-xs sm:text-sm">Сьогодні: прийом, інвентаризація, комплектація</span>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-4 gap-3 mb-5"
          style={{ contentVisibility: "auto", containIntrinsicSize: "1px 280px", willChange: "transform, opacity", transform: "translateZ(0)" }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="h-10 sm:h-12 rounded-xl sm:rounded-2xl w-full"
            style={{ boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}
            onClick={() => navigate("/material-categories")}
          >
            Нова категорія
          </Button>
          <Button
            icon={<ScanOutlined />}
            className="h-10 sm:h-12 rounded-xl sm:rounded-2xl w-full"
            style={{ boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}
            onClick={() => navigate("/scan")}
          >
            Сканування
          </Button>
          <Button
            icon={<SearchOutlined />}
            className="h-10 sm:h-12 rounded-xl sm:rounded-2xl w-full"
            style={{ boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}
            onClick={() => navigate("/search")}
          >
            Пошук
          </Button>
          <Button
            icon={<HistoryOutlined />}
            className="h-10 sm:h-12 rounded-xl sm:rounded-2xl w-full"
            style={{ boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}
            onClick={() => navigate("/history")}
          >
            Історія
          </Button>
        </motion.div>

        {/* Stats */}
        <div
          className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5"
          style={{ contentVisibility: "auto", containIntrinsicSize: "1px 320px" }}
        >
          {[
            { title: "Позицій на складі", value: 12840, icon: <DatabaseOutlined />, suffix: "шт." },
            { title: "Замовлень в обробці", value: 37, icon: <AppstoreOutlined />, suffix: "" },
            { title: "Термінові відвантаження", value: 5, icon: <FireOutlined />, suffix: "" },
          ].map((s, i) => (
            <motion.div key={s.title} variants={fadeUp} initial="hidden" animate="show" custom={i + 1} style={{ willChange: "transform, opacity", transform: "translateZ(0)" }}>
              <Card className="rounded-2xl" bodyStyle={{ padding: 16 }} style={{ boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-gray-500 text-sm mb-1">{s.title}</div>
                    <Statistic value={s.value} suffix={s.suffix} valueStyle={{ fontSize: 22, fontWeight: 700 }} />
                  </div>
                  <div className="h-9 w-9 rounded-xl bg-black text-white grid place-items-center">{s.icon}</div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Workload / Warehouse */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5"
          style={{ contentVisibility: "auto", containIntrinsicSize: "1px 560px", willChange: "transform, opacity", transform: "translateZ(0)" }}
        >
          <Card className="rounded-2xl lg:col-span-2" bodyStyle={{ padding: 16 }} style={{ boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold">Поточні операції</div>
              <Tag icon={<ClockCircleOutlined />} color="processing">Сьогодні</Tag>
            </div>
            <Divider className="my-3" />
            <ul className="space-y-3">
              {[
                { t: "Приймання постачання #A-239", k: "11:20", d: "12 палет, 820 од." },
                { t: "Комплектація замовлень B2C", k: "12:00", d: "37 замовлень" },
                { t: "Циклічна інвентаризація", k: "14:30", d: "Зона C · стелажі 12–18" },
                { t: "Відвантаження B2B", k: "16:00", d: "Маршрут №4" },
              ].map((row, i) => (
                <motion.li
                  key={row.t + i}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35 }}
                  className="flex items-start justify-between bg-white rounded-xl p-3 text-sm sm:text-base"
                  style={{ boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}
                >
                  <div>
                    <div className="font-medium">{row.t}</div>
                    <div className="text-gray-500 text-xs sm:text-sm">{row.d}</div>
                  </div>
                  <div className="text-gray-400 text-xs sm:text-sm">{row.k}</div>
                </motion.li>
              ))}
            </ul>
          </Card>

          <Card className="rounded-2xl" bodyStyle={{ padding: 16 }} style={{ boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}>
            <div className="font-semibold mb-2">Стан складу</div>
            <Divider className="my-3" />
            <div className="space-y-3 text-sm sm:text-base">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Завантаженість</span>
                <Tag color="blue">72%</Tag>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Черга відбору</span>
                <Tag color="gold">14</Tag>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">SLA відвантажень</span>
                <Tag color="green">98%</Tag>
              </div>
              <Divider className="my-3" />
              <Button
                block
                type="default"
                icon={<AppstoreOutlined />}
                onClick={() => navigate("/material-categories")}
              >
                Категорії матеріалів
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* Charts */}
        {showCharts && (
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 xl:grid-cols-3 gap-3"
            style={{ contentVisibility: "auto", containIntrinsicSize: "1px 580px", willChange: "transform, opacity", transform: "translateZ(0)" }}
          >
            <Card className="rounded-2xl" bodyStyle={{ padding: 16 }} style={{ boxShadow: "0 1px 4px rgba(0,0,0,.06)" }} title={<span className="font-semibold">Оборотність складу (міс.)</span>}>
              <div className="w-full h-[180px] sm:h-[240px]">
                <ResponsiveContainer>
                  <LineChart data={inventoryTurnover} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis domain={[0, "dataMax + 0.5"]} />
                    <RTooltip formatter={(v) => [v, "Оборотність"]} />
                    <Line type="monotone" dataKey="turnover" stroke="#111827" strokeWidth={2} dot={false} isAnimationActive={animateCharts && !prefersReducedMotion} animationDuration={900} animationBegin={0} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="rounded-2xl" bodyStyle={{ padding: 16 }} style={{ boxShadow: "0 1px 4px rgba(0,0,0,.06)" }} title={<span className="font-semibold">Замовлення за годинами</span>}>
              <div className="w-full h-[180px] sm:h-[240px]">
                <ResponsiveContainer>
                  <BarChart data={ordersPerHour} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" tickFormatter={(h) => `${h}:00`} />
                    <YAxis allowDecimals={false} />
                    <RTooltip labelFormatter={(h) => `${h}:00`} formatter={(v) => [v, "Замовл."]} />
                    <Bar dataKey="qty" radius={[6, 6, 0, 0]} fill="#0EA5E9" isAnimationActive={animateCharts && !prefersReducedMotion} animationDuration={900} animationBegin={0} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="rounded-2xl" bodyStyle={{ padding: 16 }} style={{ boxShadow: "0 1px 4px rgba(0,0,0,.06)" }} title={<span className="font-semibold">Статуси замовлень</span>}>
              <div className="w-full h-[180px] sm:h-[240px]">
                <ResponsiveContainer>
                  <PieChart>
                    <RTooltip formatter={(v, n) => [v, n]} />
                    <Legend verticalAlign="bottom" height={24} />
                    <Pie
                      data={statusSplit}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={4}
                      isAnimationActive={animateCharts && !prefersReducedMotion}
                      animationDuration={900}
                      animationBegin={0}
                    >
                      {statusSplit.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={pieColors[idx % pieColors.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Footer CTA */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={6}
          className="mt-5 mb-10"
          style={{ willChange: "transform, opacity", transform: "translateZ(0)" }}
        >
          <Card bodyStyle={{ padding: 12 }} className="rounded-2xl" style={{ boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm sm:text-base">
              <div>
                <div className="font-semibold">Прискорити роботу складу?</div>
                <div className="text-gray-500 text-sm">Підключіть сканування штрих-кодів, мітки, та швидкі сценарії.</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button icon={<ScanOutlined />} size="small" onClick={() => navigate("/scan")}>Сканер</Button>
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => navigate("/material-categories")}>
                  Додати позицію
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
